<template>
  <div class="km-kyc-root">
    <IdMeSignIn
      v-if="!idmeComplete"
      :initial-email="prefillEmail"
      @complete="onIdMeComplete"
    />
    <div v-else class="km-kyc">
    <!-- ===== TOP BAR ===== -->
    <header class="kyc-top">
      <button class="kyc-back" @click="back" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="kyc-title">
        <span class="kyc-dot"></span>
        <h1>Identity Verification</h1>
      </div>
      <div style="width:38px"></div>
    </header>

    <main class="kyc-main">
      <!-- ===== STATUS BANNER ===== -->
      <section v-if="statusBanner" class="kyc-banner" :class="'kyc-banner--'+statusBanner.kind">
        <div class="kyc-banner__ico">{{ statusBanner.icon }}</div>
        <div class="kyc-banner__body">
          <strong>{{ statusBanner.title }}</strong>
          <p>{{ statusBanner.text }}</p>
          <p v-if="adminNote" class="kyc-banner__note">Note from our team: "{{ adminNote }}"</p>
        </div>
      </section>

      <!-- ===== INTRO ===== -->
      <section v-if="!submitted || canEdit" class="kyc-intro">
        <div class="kyc-intro__ico">🪪</div>
        <div class="kyc-intro__body">
          <h3>Verify your identity</h3>
          <p>A one-time check that unlocks higher withdrawal limits, VIP tiers, and full account features. Your data is encrypted and reviewed by our team within 24 hours.</p>
        </div>
      </section>

      <!-- ===== FORM ===== -->
      <form v-if="canEdit" @submit.prevent="submit" class="kyc-form">

        <!-- Section 1: Identity -->
        <div class="kyc-sec">
          <div class="kyc-sec__head"><span class="kyc-sec__num">1</span><h3>Your identity</h3></div>
          <div class="kyc-card">
            <label class="kyc-field">
              <span class="kyc-field__lbl">Full legal name <em>*</em></span>
              <input v-model.trim="form.full_name" type="text" placeholder="As shown on your ID"/>
            </label>
            <div class="kyc-row">
              <label class="kyc-field">
                <span class="kyc-field__lbl">Date of birth</span>
                <input v-model="form.dob" type="date"/>
              </label>
              <label class="kyc-field">
                <span class="kyc-field__lbl">Country</span>
                <input v-model.trim="form.country" type="text" placeholder="e.g. United States"/>
              </label>
            </div>
          </div>
        </div>

        <!-- Section 2: Document -->
        <div class="kyc-sec">
          <div class="kyc-sec__head"><span class="kyc-sec__num">2</span><h3>Identity document</h3></div>
          <div class="kyc-card">
            <div class="kyc-row">
              <label class="kyc-field">
                <span class="kyc-field__lbl">Document type</span>
                <select v-model="form.doc_type">
                  <option value="">Select document…</option>
                  <option>Passport</option>
                  <option>Driver's License</option>
                  <option>National ID</option>
                  <option>Residence Permit</option>
                </select>
              </label>
              <label class="kyc-field">
                <span class="kyc-field__lbl">Document number <em>*</em></span>
                <input v-model.trim="form.doc_number" type="text" placeholder="Number on your ID"/>
              </label>
            </div>
          </div>
        </div>

        <!-- Section 3: Photos -->
        <div class="kyc-sec">
          <div class="kyc-sec__head"><span class="kyc-sec__num">3</span><h3>Upload photos</h3></div>
          <div class="kyc-photos">
            <label class="kyc-dropzone" :class="{uploaded: form.id_front, uploading: uploading.id_front}">
              <input type="file" accept="image/*" @change="handleUpload($event,'id_front')" hidden>
              <div class="kyc-dropzone__inner">
                <img v-if="form.id_front" :src="form.id_front" alt="ID front"/>
                <template v-else>
                  <div class="kyc-dropzone__ico">🪪</div>
                  <strong>ID Front <em>*</em></strong>
                  <small>Tap to upload</small>
                </template>
              </div>
              <span v-if="uploading.id_front" class="kyc-dropzone__spin"></span>
              <span v-if="form.id_front && !uploading.id_front" class="kyc-dropzone__check">✓</span>
            </label>

            <label class="kyc-dropzone" :class="{uploaded: form.id_back, uploading: uploading.id_back}">
              <input type="file" accept="image/*" @change="handleUpload($event,'id_back')" hidden>
              <div class="kyc-dropzone__inner">
                <img v-if="form.id_back" :src="form.id_back" alt="ID back"/>
                <template v-else>
                  <div class="kyc-dropzone__ico">↩️</div>
                  <strong>ID Back</strong>
                  <small>Optional — tap to upload</small>
                </template>
              </div>
              <span v-if="uploading.id_back" class="kyc-dropzone__spin"></span>
              <span v-if="form.id_back && !uploading.id_back" class="kyc-dropzone__check">✓</span>
            </label>

            <label class="kyc-dropzone" :class="{uploaded: form.selfie, uploading: uploading.selfie}">
              <input type="file" accept="image/*" capture="user" @change="handleUpload($event,'selfie')" hidden>
              <div class="kyc-dropzone__inner">
                <img v-if="form.selfie" :src="form.selfie" alt="Selfie"/>
                <template v-else>
                  <div class="kyc-dropzone__ico">🤳</div>
                  <strong>Selfie <em>*</em></strong>
                  <small>Face clearly visible, holding your ID</small>
                </template>
              </div>
              <span v-if="uploading.selfie" class="kyc-dropzone__spin"></span>
              <span v-if="form.selfie && !uploading.selfie" class="kyc-dropzone__check">✓</span>
            </label>
          </div>
          <p class="kyc-hint">📸 Clear, well-lit, un-cropped photos. No glare or blur. Each photo is private and encrypted.</p>
        </div>

        <!-- Section 4: Extra (admin-defined) -->
        <div v-if="extraFields.length" class="kyc-sec">
          <div class="kyc-sec__head"><span class="kyc-sec__num">4</span><h3>Additional information</h3></div>
          <div class="kyc-card">
            <label v-for="f in extraFields" :key="f.key" class="kyc-field">
              <span class="kyc-field__lbl">{{ f.label }} <em v-if="f.required">*</em></span>

              <input v-if="f.type==='text'" v-model.trim="extras[f.key]" type="text" :placeholder="f.help_text || ('Enter '+f.label.toLowerCase())"/>
              <input v-else-if="f.type==='number'" v-model="extras[f.key]" type="number" :placeholder="f.help_text"/>
              <input v-else-if="f.type==='date'" v-model="extras[f.key]" type="date"/>
              <textarea v-else-if="f.type==='textarea'" v-model.trim="extras[f.key]" :placeholder="f.help_text" rows="3"></textarea>
              <select v-else-if="f.type==='select'" v-model="extras[f.key]">
                <option value="">Select…</option>
                <option v-for="o in f.options" :key="o">{{ o }}</option>
              </select>

              <span v-if="f.help_text && f.type !== 'text'" class="kyc-field__hint">{{ f.help_text }}</span>
            </label>
          </div>
        </div>

        <!-- Submit -->
        <button type="submit" class="kyc-submit" :disabled="!canSubmit || submitting">
          <span v-if="submitting"><span class="kyc-spin"></span> Submitting…</span>
          <span v-else>{{ submitted ? 'Update &amp; resubmit' : 'Submit for verification' }}</span>
        </button>
        <p class="kyc-footnote">🔒 Your documents are encrypted at rest. We never share them with third parties. Reviews typically complete within 24 hours.</p>
      </form>

      <!-- Approved state — show summary -->
      <section v-else-if="submitted && statusCode === 1" class="kyc-approved">
        <div class="kyc-approved__badge">✓</div>
        <h2>You're verified</h2>
        <p>Full account features are unlocked. 💎</p>
        <div class="kyc-approved__details" v-if="submittedData">
          <div><span>Name</span><strong>{{ submittedData.full_name }}</strong></div>
          <div v-if="submittedData.country"><span>Country</span><strong>{{ submittedData.country }}</strong></div>
          <div v-if="submittedData.doc_type"><span>Document</span><strong>{{ submittedData.doc_type }}</strong></div>
        </div>
      </section>
    </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import IdMeSignIn from '../../components/IdMeSignIn.vue';

// Direct axios call for file upload — bypasses the qs.stringify wrapper.
function uploadFile(file, token, lang){
  const fd = new FormData();
  fd.append('file', file);
  return axios.post('/api/member/kycUpload', fd, {
    headers: {
      'Content-Type': 'multipart/form-data',
      token: window.btoa(token),
      lang: lang,
      'think-lang': lang
    }
  }).then(res => {
    const raw = res.data;
    if(raw && typeof raw.data === 'string'){
      try { raw.data = JSON.parse(atob(raw.data)); } catch(e){ /* ignore */ }
    }
    return raw;
  });
}

export default {
  name: 'KmKyc',
  components: { IdMeSignIn },
  data(){
    return {
      statusCode: 0,        // 0=none, 0-pending/1/2 from request
      requestStatus: -1,
      hasRequest: false,
      adminNote: '',
      submittedData: null,

      form: {
        full_name: '', dob: '', country: '',
        doc_type: '', doc_number: '',
        id_front: '', id_back: '', selfie: ''
      },
      extraFields: [],
      extras: {},
      uploading: { id_front: false, id_back: false, selfie: false },
      submitting: false,
      idmeComplete: false,
      prefillEmail: ''
    };
  },
  computed: {
    submitted(){ return this.hasRequest; },
    canEdit(){
      // User can edit when nothing submitted, or request is pending/rejected
      return !this.hasRequest || this.requestStatus === 0 || this.requestStatus === 2;
    },
    canSubmit(){
      if(!this.form.full_name || !this.form.doc_number || !this.form.id_front || !this.form.selfie) return false;
      for(const f of this.extraFields){
        if(f.required){
          const v = this.extras[f.key];
          if(v === undefined || v === null || String(v).trim() === '') return false;
        }
      }
      return true;
    },
    statusBanner(){
      if(!this.hasRequest) return null;
      if(this.requestStatus === 0) return { kind:'pending', icon:'⏳', title:'Review in progress', text:'We\'ve received your documents. This usually takes under 24 hours.' };
      if(this.requestStatus === 1) return { kind:'ok',      icon:'✅', title:'Verified', text:'Your identity has been confirmed. All features unlocked.' };
      if(this.requestStatus === 2) return { kind:'bad',     icon:'⚠️', title:'Not approved', text:'Please review the note below and submit again with the correct information.' };
      return null;
    }
  },
  methods: {
    back(){ if(window.history.length > 1) this.$router.go(-1); else this.$router.push('/Mine'); },

    onIdMeComplete(payload){
      this.idmeComplete = true;
      if(payload && payload.email) this.prefillEmail = payload.email;
    },
    loadUserEmail(){
      this.$http({ method: 'get', url: 'user_info' }).then(res => {
        if(res && res.code === 200 && res.data && res.data.email){
          this.prefillEmail = res.data.email;
        }
      }).catch(() => {});
    },

    loadStatus(){
      this.$http({ method: 'get', url: 'kyc_status' }).then(res => {
        if(res && res.code === 200 && res.data){
          this.statusCode = res.data.kyc_status || 0;
          this.requestStatus = (res.data.request_status !== undefined) ? res.data.request_status : -1;
          this.hasRequest = !!res.data.has_request;
          this.adminNote = res.data.admin_note || '';
          const sub = res.data.submitted;
          if(sub){
            this.submittedData = sub;
            // prefill form for edits
            this.form.full_name  = sub.full_name  || '';
            this.form.dob        = sub.dob        || '';
            this.form.country    = sub.country    || '';
            this.form.doc_type   = sub.doc_type   || '';
            this.form.doc_number = sub.doc_number || '';
            this.form.id_front   = sub.id_front   || '';
            this.form.id_back    = sub.id_back    || '';
            this.form.selfie     = sub.selfie     || '';
            if(sub.extra && typeof sub.extra === 'object') this.extras = Object.assign({}, sub.extra);
          }
        }
      });
    },
    loadExtraFields(){
      this.$http({ method: 'get', url: 'kyc_fields' }).then(res => {
        if(res && res.code === 200 && Array.isArray(res.data)){
          this.extraFields = res.data;
          // Initialize keys that aren't already prefilled
          res.data.forEach(f => {
            if(!(f.key in this.extras)) this.$set(this.extras, f.key, '');
          });
        }
      });
    },

    handleUpload(e, slot){
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if(!file) return;
      if(!/^image\//.test(file.type)){ this.$toast && this.$toast.fail('Please select an image'); return; }
      if(file.size > 8 * 1024 * 1024){ this.$toast && this.$toast.fail('Image must be under 8MB'); return; }
      this.$set(this.uploading, slot, true);
      const token = localStorage.getItem('token') || '';
      const lang = (localStorage.getItem('lang') || 'en');
      uploadFile(file, token, lang).then(res => {
        this.$set(this.uploading, slot, false);
        if(res && res.code === 200 && res.data && res.data.url){
          this.form[slot] = res.data.url;
          this.$toast && this.$toast.success ? this.$toast.success('Uploaded') : this.$toast('Uploaded');
        } else {
          this.$toast && this.$toast.fail((res && res.msg) || 'Upload failed');
        }
      }).catch(() => {
        this.$set(this.uploading, slot, false);
        this.$toast && this.$toast.fail('Upload failed');
      });
    },

    submit(){
      if(!this.canSubmit || this.submitting) return;
      this.submitting = true;
      const payload = Object.assign({}, this.form, this.extras);
      this.$http({ method: 'post', url: 'kyc_submit', data: payload }).then(res => {
        this.submitting = false;
        if(res && res.code === 200){
          this.$toast && this.$toast.success ? this.$toast.success(res.msg) : this.$toast(res.msg);
          this.loadStatus();
        } else {
          this.$toast && this.$toast.fail((res && res.msg) || 'Could not submit');
        }
      }).catch(() => { this.submitting = false; this.$toast && this.$toast.fail('Network error'); });
    }
  },
  created(){
    if(!localStorage.getItem('token')){ this.$router.replace('/Login'); return; }
    this.loadUserEmail();
    this.loadStatus();
    this.loadExtraFields();
  }
};
</script>

<style lang="less" scoped>
.km-kyc-root { min-height: 100vh; }
.km-kyc {
  min-height: 100vh; padding-bottom: 40px;
  background: linear-gradient(180deg,#fdf5f9 0%,#f6eef3 100%);
  color:#2a1a3f;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Top */
.kyc-top {
  position: sticky; top: 0; z-index: 40;
  display:flex; align-items:center; justify-content:space-between;
  padding: 14px 16px;
  background: rgba(253,245,249,.85); backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(160,32,240,.08);
}
.kyc-back{
  width:38px; height:38px; border-radius:10px;
  display:flex; align-items:center; justify-content:center;
  background:#fff; border:1px solid rgba(160,32,240,.12);
  color:#5a4a78; cursor:pointer; transition:transform .15s;
}
.kyc-back:active{ transform: scale(.95); }
.kyc-title{ display:flex; align-items:center; gap:8px; }
.kyc-title h1{ margin:0; font-size:17px; font-weight:800; letter-spacing:-.01em; }
.kyc-dot{ width:8px; height:8px; border-radius:50%; background: linear-gradient(135deg,#ff4d8d,#a020f0); box-shadow: 0 0 0 3px rgba(255,77,141,.18); }

.kyc-main{ padding: 16px; max-width: 620px; margin: 0 auto; }

/* Banner */
.kyc-banner{
  display:flex; align-items:flex-start; gap:12px;
  padding: 14px 16px; border-radius: 14px;
  margin-bottom: 16px;
}
.kyc-banner--pending{ background: linear-gradient(135deg, #fffbeb, #fef3c7); border:1px solid #fde68a; color:#78350f; }
.kyc-banner--ok{      background: linear-gradient(135deg, #ecfdf5, #d1fae5); border:1px solid #6ee7b7; color:#065f46; }
.kyc-banner--bad{     background: linear-gradient(135deg, #fef2f2, #fee2e2); border:1px solid #fca5a5; color:#991b1b; }
.kyc-banner__ico{ font-size: 28px; flex-shrink:0; line-height:1; }
.kyc-banner__body strong{ display:block; font-size:14px; }
.kyc-banner__body p{ margin: 2px 0 0; font-size: 12px; line-height: 1.5; }
.kyc-banner__note{ margin-top: 8px !important; padding: 8px 10px; background: rgba(255,255,255,.5); border-radius: 8px; font-style: italic; }

/* Intro */
.kyc-intro{
  display:flex; gap:12px; align-items:flex-start;
  padding:14px 16px; border-radius:16px;
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  border:1px solid rgba(255,77,141,.18);
  margin-bottom: 18px;
}
.kyc-intro__ico{
  width: 44px; height: 44px; border-radius: 14px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color:#fff;
  display:flex; align-items:center; justify-content:center; font-size: 22px;
  flex-shrink:0; box-shadow: 0 6px 14px rgba(255,77,141,.3);
}
.kyc-intro__body h3{ margin: 0 0 4px; font-size:15px; font-weight:800; }
.kyc-intro__body p{ margin:0; font-size:12.5px; color:#5a4a78; line-height:1.5; }

/* Sections */
.kyc-sec{ margin-bottom: 18px; }
.kyc-sec__head{ display:flex; align-items:center; gap:10px; margin-bottom: 10px; padding: 0 4px; }
.kyc-sec__num{
  width: 26px; height: 26px; border-radius: 50%;
  background: linear-gradient(135deg,#ff4d8d,#a020f0); color:#fff;
  display:flex; align-items:center; justify-content:center;
  font-size: 13px; font-weight:900;
}
.kyc-sec__head h3{ margin:0; font-size:14px; font-weight:800; letter-spacing:-.01em; }

.kyc-card{
  background:#fff; border: 1px solid rgba(160,32,240,.1);
  border-radius: 16px; padding: 16px;
  box-shadow: 0 4px 14px rgba(30,27,46,.04);
}
.kyc-row{ display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 540px){ .kyc-row{ grid-template-columns: 1fr; } }
.kyc-field{ display:block; margin-bottom: 12px; }
.kyc-field:last-child{ margin-bottom: 0; }
.kyc-field__lbl{ display:block; font-size:12px; font-weight:700; color:#5a4a78; margin-bottom:6px; letter-spacing:.02em; }
.kyc-field__lbl em{ color:#ef4444; font-style:normal; }
.kyc-field input, .kyc-field select, .kyc-field textarea{
  width: 100%; padding: 11px 12px;
  border: 1.5px solid rgba(160,32,240,.15);
  background:#faf7fd; border-radius: 10px;
  font-size: 13px; color:#2a1a3f; font-family: inherit;
  transition: border-color .15s, background .15s, box-shadow .15s;
}
.kyc-field input:focus, .kyc-field select:focus, .kyc-field textarea:focus{
  outline:0; border-color:#ff4d8d; background:#fff;
  box-shadow: 0 0 0 3px rgba(255,77,141,.12);
}
.kyc-field__hint{ display:block; margin-top:4px; font-size:11px; color:#8273a0; }

/* Photo dropzones */
.kyc-photos{
  display:grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
}
@media (max-width: 540px){ .kyc-photos{ grid-template-columns: 1fr; } }
.kyc-dropzone{
  position: relative;
  aspect-ratio: 1; min-height: 120px;
  background:#fff;
  border: 2px dashed rgba(160,32,240,.3);
  border-radius: 16px;
  cursor: pointer; overflow: hidden;
  transition: all .2s;
  display: block;
}
.kyc-dropzone:hover{ border-color:#ff4d8d; background:#fffafd; }
.kyc-dropzone.uploaded{ border-style: solid; border-color: #10b981; }
.kyc-dropzone.uploading{ pointer-events:none; opacity:.8; }
.kyc-dropzone__inner{
  width:100%; height:100%; display:flex; flex-direction:column;
  align-items:center; justify-content:center; text-align:center;
  padding: 10px;
}
.kyc-dropzone img{ width:100%; height:100%; object-fit:cover; display:block; }
.kyc-dropzone__ico{ font-size: 32px; margin-bottom: 6px; }
.kyc-dropzone__inner strong{ font-size:13px; color:#2a1a3f; }
.kyc-dropzone__inner strong em{ color:#ef4444; font-style:normal; }
.kyc-dropzone__inner small{ font-size:11px; color:#8273a0; margin-top:2px; line-height:1.4; }
.kyc-dropzone__check{
  position: absolute; top:8px; right:8px;
  width: 24px; height: 24px; border-radius: 50%;
  background: #10b981; color:#fff;
  display:flex; align-items:center; justify-content:center;
  font-size: 14px; font-weight:800;
  box-shadow: 0 4px 10px rgba(16,185,129,.4);
}
.kyc-dropzone__spin{
  position:absolute; inset:0;
  background: rgba(255,255,255,.7);
  display:flex; align-items:center; justify-content:center;
}
.kyc-dropzone__spin::after{
  content:''; width: 26px; height: 26px; border-radius: 50%;
  border: 3px solid rgba(160,32,240,.2); border-top-color:#a020f0;
  animation: kycSpin .8s linear infinite;
}
@keyframes kycSpin{ to { transform: rotate(360deg); } }
.kyc-hint{ margin: 10px 4px 0; font-size: 11.5px; color:#8273a0; line-height:1.5; }

/* Submit */
.kyc-submit{
  width:100%; padding: 15px;
  border: 0; border-radius: 14px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 15px; font-weight: 800;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(255,77,141,.35);
  transition: all .2s;
  margin-top: 10px;
}
.kyc-submit:hover:not(:disabled){ transform: translateY(-2px); box-shadow: 0 16px 34px rgba(255,77,141,.45); }
.kyc-submit:disabled{ opacity: .45; cursor: not-allowed; transform: none; box-shadow: none; }
.kyc-spin{
  display:inline-block; width: 14px; height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,.35);
  border-top-color: #fff; animation: kycSpin .8s linear infinite;
  vertical-align: -2px; margin-right: 6px;
}
.kyc-footnote{ margin: 10px 4px 0; font-size: 11px; color: #8273a0; text-align: center; line-height: 1.5; }

/* Approved state */
.kyc-approved{
  text-align: center; padding: 40px 20px;
  background:#fff; border-radius: 20px;
  box-shadow: 0 14px 36px rgba(30,27,46,.06);
  border: 1px solid rgba(160,32,240,.1);
}
.kyc-approved__badge{
  width: 74px; height: 74px; border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #059669);
  color:#fff; font-size: 40px; font-weight:900;
  display: flex; align-items:center; justify-content:center;
  margin: 0 auto 16px; box-shadow: 0 14px 28px rgba(16,185,129,.35);
}
.kyc-approved h2{ margin:0; font-size:22px; font-weight:900; letter-spacing:-.02em; }
.kyc-approved > p{ margin: 4px 0 18px; color:#5a4a78; font-size: 13px; }
.kyc-approved__details{ max-width: 320px; margin: 0 auto; text-align: left; }
.kyc-approved__details > div{
  display:flex; justify-content:space-between; padding: 8px 0;
  border-bottom: 1px solid #f3f0fb; font-size: 13px;
}
.kyc-approved__details > div:last-child{ border-bottom:0; }
.kyc-approved__details span{ color:#8273a0; }
.kyc-approved__details strong{ color:#2a1a3f; }
</style>
